import { useCallback, useEffect, useMemo, useState } from 'react';
import { SymbolChart } from 'common/interfaces/api';
import { cn, useMediaQuery } from 'common/utils';
import { SearchIcon } from 'components/v2/atoms/icon';

interface SymbolSelectionDialogProps {
  isOpen: boolean;
  onSelect: (symbolsIds: number[]) => void;
  selectedSymbols: SymbolChart[];
  allSymbols: SymbolChart[];
  triggerRef?: React.RefObject<HTMLDivElement>;
}

const MAX_SYM = 3;

const SymbolSelectionDialog = ({
  isOpen,
  onSelect,
  selectedSymbols,
  allSymbols,
  triggerRef,
}: SymbolSelectionDialogProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('すべて');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const isMobile = useMediaQuery('(max-width: 767px)');
  const categories = useMemo(() => {
    return ['すべて', ...new Set(allSymbols.map((symbol) => symbol.type))];
  }, [allSymbols]);

  // Calculate dropdown position with proper viewport handling
  const calculatePosition = useCallback(() => {
    if (!triggerRef?.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 371; // Approximate height of dropdown
    const dropdownWidth = isMobile ? window.innerWidth - 32 : 400; // Match className widths
    const spacing = 8; // Spacing between trigger and dropdown
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = rect.top - dropdownHeight - spacing;
    let left = rect.left;

    // Adjust for mobile
    if (isMobile) {
      left = 16; // Center with 16px margins (calc(100vw-32px))
    } else {
      // Ensure dropdown doesn't go off the right edge
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 16; // 16px margin from edge
      }

      // Ensure dropdown doesn't go off the left edge
      if (left < 16) {
        left = 16;
      }
    }

    // If not enough space above, position below the trigger
    if (top < 16) {
      // 16px margin from top
      top = rect.bottom + spacing;

      // If positioning below would also go off screen, position in viewport center
      if (top + dropdownHeight > viewportHeight - 16) {
        top = Math.max(16, (viewportHeight - dropdownHeight) / 2);
      }
    }

    setPosition({ top, left });
  }, [isMobile, triggerRef]);

  useEffect(() => {
    calculatePosition();
  }, [isOpen, calculatePosition]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, triggerRef, isMobile, calculatePosition]);

  const filteredSymbols = allSymbols.filter((symbol) => {
    const matchesSearch =
      symbol.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      symbol.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      activeCategory === 'すべて' || symbol.type === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSymbolToggle = (symbol: SymbolChart) => {
    const isSelected = selectedSymbols.some((s) => s.id === symbol.id);
    let newSelectedSymbols: SymbolChart[];

    if (isSelected) {
      newSelectedSymbols = selectedSymbols.filter((s) => s.id !== symbol.id);
    } else {
      if (selectedSymbols.length >= MAX_SYM) {
        return; // Max 3 symbols
      }
      newSelectedSymbols = [...selectedSymbols, symbol];
    }

    onSelect(newSelectedSymbols.map((s) => s.id));
  };

  const isSymbolSelected = (symbolId: number) => {
    return selectedSymbols.some((s) => s.id === symbolId);
  };

  if (!isOpen) return null;

  return (
    <div
      className="all-font-sans fixed z-50 w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] rounded-xl border border-gray-200 bg-white shadow-lg sm:w-[400px] sm:max-w-[400px]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {/* Header */}
      <div className="rounded-t-xl border-b border-pp-border-primary bg-[#F8F9FA] p-3">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-pp-text-primary">
            マーケットを選択
          </h3>
          <div className="text-xs text-pp-text-secondary">
            ※最大{MAX_SYM}つまで選択可能
          </div>
        </div>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-pp-text-secondary" />
          <input
            type="text"
            placeholder="マーケットを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-pp-border-primary bg-white py-2 pl-9 pr-4 text-sm focus:border-pp-text-tertiary focus:outline-none"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="border-b border-pp-border-primary px-4 py-2">
        <div className="scrollbar-hide flex space-x-2 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-light transition-colors ${
                activeCategory === category
                  ? 'bg-pp-text-primary text-white'
                  : 'bg-pp-bg-tertiary text-pp-text-primary hover:bg-pp-border-primary'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Symbols List */}
      <div className="scrollbar-default max-h-56 overflow-y-auto">
        {filteredSymbols.map((symbol) => (
          <button
            key={symbol.id}
            onClick={() => handleSymbolToggle(symbol)}
            disabled={
              !isSymbolSelected(symbol.id) && selectedSymbols.length >= MAX_SYM
            }
            className={cn(
              `flex w-full items-center px-4 py-2 text-left transition-colors hover:bg-pp-bg-tertiary ${
                !isSymbolSelected(symbol.id) &&
                selectedSymbols.length >= MAX_SYM
                  ? 'cursor-not-allowed opacity-50'
                  : ''
              }`,
              {
                'bg-pp-green-subtle': isSymbolSelected(symbol.id),
              },
            )}
          >
            <div className="flex-1">
              <div className="text-sm font-medium text-pp-text-primary">
                {symbol.name}
              </div>
            </div>

            {/* Checkbox */}
            <div className="ml-3">
              <div
                className={`flex size-4 items-center justify-center rounded border-2 transition-colors ${
                  isSymbolSelected(symbol.id)
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {isSymbolSelected(symbol.id) && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-2.5 text-white"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {filteredSymbols.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          検索結果が見つかりません
        </div>
      )}
    </div>
  );
};

export default SymbolSelectionDialog;
